import { CustomResponse } from '@/app/models';
import { UserSession } from '@/app/models/interface/user.session';
import { environment } from '@/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import { handleError } from '../tools/handle-error.tools';
import { CookieService } from 'ngx-cookie-service';
import { REFRESH_TOKEN } from '../keys/key';

interface SessionData {
  sessions: UserSession[];
  activas: number;
  inactivas: number;
}

interface TwoFAStatus {
  success: boolean;
  message: string;
  verified: boolean;
  ttl: number; // TTL de redis es en segundos
  codeActive: boolean;
}

@Injectable({ providedIn: 'root' })
export class SessionService {
  private http = inject(HttpClient);
  private url = environment.api + 'sessions';
  private cookie = inject(CookieService);

  findAll() {
    return this.http.get<CustomResponse<SessionData>>(this.url).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }

  revokeSession(refreshToken: string) {
    const url = this.url + '/revoke/' + refreshToken;

    return this.http.delete<CustomResponse<UserSession>>(url).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }

  // Caché del estado 2FA.
  private codeStatusRequest: Observable<TwoFAStatus> | null = null;
  private codeStatusResult: TwoFAStatus | null = null;
  private codeStatusExpires = 0;

  // Vigencias.
  private readonly CODE_TTL = 10 * 60 * 1000;
  private VERIFICATION_TTL = 2 * 60 * 60 * 1000;

  private get refreshToken(): string {
    return this.cookie.get(REFRESH_TOKEN);
  }

  twoFAStatus(params: HttpParams): Observable<TwoFAStatus> {
    const url = this.url + '/2fa/code';

    return this.http
      .post<CustomResponse<TwoFAStatus>>(
        url,
        {
          refreshToken: this.refreshToken,
        },
        { params },
      )
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => data.result),
      );
  }

  twoFAVerify(code: string): Observable<TwoFAStatus> {
    const url = this.url + '/2fa/verify';

    return this.http
      .post<CustomResponse<TwoFAStatus>>(url, {
        refreshToken: this.refreshToken,
        code,
      })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => data.result),
        tap((data) => {
          if (data.success && data.verified) {
            this.VERIFICATION_TTL = data.ttl * 1000; //# Lo pasamos a MS;
            this.setVerificationCache();
          }
        }),
      );
  }

  /**
   * Consulta el estado 2FA.
   * Evita solicitudes repetidas durante la vigencia correspondiente.
   */
  checkTwoFAStatus(params: HttpParams, force = false): Observable<TwoFAStatus> {
    if (force) {
      this.clearTwoFACache();
    }

    // Ya existe una petición en curso.
    if (this.codeStatusRequest) {
      return this.codeStatusRequest;
    }

    // La verificación sigue vigente.
    if (
      this.codeStatusResult?.verified &&
      Date.now() < this.codeStatusExpires
    ) {
      return of(this.codeStatusResult);
    }

    // Existe un código enviado que todavía está vigente.
    if (
      this.codeStatusResult?.codeActive &&
      Date.now() < this.codeStatusExpires
    ) {
      return of(this.codeStatusResult);
    }

    this.codeStatusRequest = this.twoFAStatus(params).pipe(
      tap((data) => {
        this.VERIFICATION_TTL = data.ttl * 1000; //# Lo pasamos a MS;


        this.codeStatusResult = data;

        this.codeStatusExpires =
          Date.now() + (data.verified ? this.VERIFICATION_TTL : this.CODE_TTL);
      }),
      finalize(() => {
        this.codeStatusRequest = null;
      }),
      shareReplay(1),
    );

    return this.codeStatusRequest;
  }

  /**
   * Guarda una verificación exitosa durante 2 horas.
   */
  private setVerificationCache(): void {
    this.codeStatusResult = {
      success: true,
      message: 'Verificación exitosa',
      verified: true,
      ttl: this.VERIFICATION_TTL,
      codeActive: false,
    };

    this.codeStatusExpires = Date.now() + this.VERIFICATION_TTL;
  }

  /**
   * Invalida la caché.
   */
  clearTwoFACache(): void {
    this.codeStatusResult = null;
    this.codeStatusExpires = 0;
  }
}
